import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  Check,
  Crown,
  Eye,
  Film,
  HelpCircle,
  Megaphone,
  MessageCircle,
  Palette,
  Rocket,
  Scissors,
  Sparkles,
  Star,
  Video,
  Zap,
} from "lucide-react";
import TheNav from "../Components/navbar";
import SEO from "../Components/SEO";
import { useAuth } from "../Context/AuthContext";
import { db } from "../Context/firebaseConfig";
import { getOrCreateDirectConversation } from "../utils/chatNavigation";
import {
  createPricingCheckoutSession,
  toBackendPlanId,
  toBackendProductId,
} from "../utils/pricingCheckout";
import {
  clearCheckoutStatusParams,
  getCheckoutStatusToast,
} from "../utils/checkoutStatus";
import {
  getRequiredRoleForPlan,
  getRequiredRoleForProduct,
  getRoleMismatchMessage,
  getUserRole,
} from "../utils/subscriptionAccount";
import { assignSafeExternalUrl } from "../utils/security";

const MINT = "#10b981";
const PURPLE = "#a855f7";
const COACHING_ACCOUNT_ID = "bY6ZB36al8fIXrChSL0FmedAFo92";
const MAX_ADVISORY_DRAFT =
  "Hi, I'm interested in Max Advisory and the 6% revenue share plan.";

const PLAN_PREVIEWS = {
  "free-creator": "basic creator access, browsing, messaging, and orders.",
  "creator-plus":
    "reusable instructions, project organization, priority profile, and more.",
  "creator-studio":
    "batch order creation, a content workflow board, discounted reviews, and more.",
  "coaching-starter":
    "monthly coaching access, accountability, and direct creator feedback.",
  "coaching-growth":
    "deeper 1:1 support, more frequent access, and stronger creator strategy.",
  "coaching-scale":
    "higher-touch growth planning, workflow support, and hands-on guidance.",
  "coaching-elite":
    "premium monthly advisory support with close strategic access.",
  "free-editor": "basic editor profile, portfolio, messaging, and search placement.",
  "editor-pro":
    "20 portfolio videos, premium profile tools, featured status, Portfolio Statistics, and more.",
  "editor-elite":
    "homepage spotlight eligibility, priority review, advanced customization, and more.",
};

const SERVICE_PREVIEWS = {
  "one-on-one": "Book a Call for one hour to discuss content issues.",
  "portfolio-review": "create a manual portfolio review request.",
  "creator-channel-review": "create a creator channel review request.",
  //"clipper-strategy": "create a temporary featured spotlight request.",
};

const PLAN_DATA = {
  creators: [
    {
      id: "free-creator",
      name: "Free Creator",
      description: "Everything needed to use Kre8.",
      monthlyPrice: 0,
      accent: "neutral",
      cta: "You are here",
      icon: Video,
      features: [
        "Browse editors and clippers",
        "Message editors",
        "Join clipping campaign early access",
        "Connect YouTube",
        "Display YouTube videos",
        "Basic creator profile",
      ],
    },
    {
      id: "creator-plus",
      name: "Creator Plus",
      description: "For creators who want to manage editing and growth content more efficiently.",
      monthlyPrice: 9,
      accent: "mint",
      cta: "Upgrade to Creator Plus",
      icon: Sparkles,
      features: [
        "Reusable editing instruction templates",
        "Project organization tools",
        "Priority Creator badge",
        "Favorite editors",
        "Early access to campaign tools",
        "Early access to new creator features",
      ],
    },
    {
      id: "creator-studio",
      name: "Creator Studio",
      description: "For creators posting consistently.",
      monthlyPrice: 19,
      accent: "purple",
      cta: "Choose Creator Studio",
      icon: Crown,
      features: [
        "Everything in Creator Plus",
        "Batch order creation",
        "Content workflow board",
        "Discounted Creator Review",
      ],
    },
  ],
  editors: [
    {
      id: "free-editor",
      name: "Free Editor or Clipper",
      description: "Everything needed to build a profile and talk with creators.",
      monthlyPrice: 0,
      accent: "neutral",
      cta: "You are here",
      icon: Film,
      features: [
        "Public editor or clipper profile",
        "Creator messaging",
        "3 portfolio videos",
        "Clipping campaign early access",
        "Basic profile customization",
      ],
    },
    {
      id: "editor-pro",
      name: "Editor Pro",
      description: "Best for editors and clippers who want a stronger profile.",
      monthlyPrice: 14.99,
      accent: "mint",
      cta: "Upgrade to Editor Pro",
      badge: "Most Popular",
      icon: Star,
      features: [
        "20 portfolio videos",
        "Premium profile banner x themes",
        "Featured Editor badge",
        "Clipper profile positioning",
        "Higher search placement eligibility",
        "Analytics Lite",
        "Portfolio Statistics",
      ],
    },
    {
      id: "editor-elite",
      name: "Editor Elite",
      description: "For serious editors and clippers building recurring creator relationships.",
      monthlyPrice: 29.99,
      accent: "purple",
      cta: "Choose Editor Elite",
      icon: Rocket,
      features: [
        "Everything in Editor Pro",
        "Homepage spotlight eligibility",
        "Priority profile review",
        "Advanced profile customization",
        "Discounted Portfolio Review",
        "Highest featured placement priority",
      ],
    },
  ],
};

const ONE_TIME_SERVICES = [
    {
    id: "one-on-one",
    title: "Book a Call",
    audience: "Everyone",
    price: "$50",
    description:
      "One hour, more efficient for people with below a $500 budget. ONLY AFTER we have completed the first 2 free calls.",
    cta: "Book now",
    icon: BadgeCheck,
  },
  {
    id: "portfolio-review",
    title: "Portfolio Review",
    audience: "For editors",
    price: "$29",
    description:
      "Get a practical review of your editor profile, portfolio, pricing, and positioning so creators are more likely to message you.",
    cta: "Request Review",
    icon: Palette,
  },
  {
    id: "creator-channel-review",
    title: "Creator Channel Review",
    audience: "For creators",
    price: "$29",
    description:
      "Get a manual review of your content, editing strategy, upload workflow, and hiring recommendations.",
    cta: "Request Review",
    icon: Video,
  },
  // {
  //   id: "featured-spotlight",
  //   title: "Featured Spotlight",
  //   audience: "For editors",
  //   price: "$5/week",
  //   description:
  //     "Get featured placement for 7 days so more creators can discover you.",
  //   cta: "Get Spotlight",
  //   icon: Megaphone,
  // },
];

const COACHING_PLANS = [
  {
    id: "coaching-starter",
    name: "Starter Coaching",
    accent: "mint",
    icon: MessageCircle,
    description: "A low-friction entry point for smaller creators who want direct guidance.",
    cta: "Start Starter Coaching",
    checkoutEnabled: true,
  },
  {
    id: "coaching-growth",
    name: "Growth Coaching",
    accent: "neutral",
    icon: Sparkles,
    description: "A mid-tier coaching plan for creators actively trying to grow faster.",
    cta: "Choose Growth Coaching",
    checkoutEnabled: true,
  },
  {
    id: "coaching-scale",
    name: "Scale Coaching",
    accent: "purple",
    icon: Rocket,
    description: "For creators treating content like a business and needing deeper strategy.",
    cta: "Choose Scale Coaching",
    checkoutEnabled: true,
  },
  {
    id: "coaching-elite",
    name: "Elite Advisory",
    accent: "purple",
    icon: Crown,
    description: "A premium advisory tier for larger creators who want close access and direct strategy.",
    cta: "Choose Elite Advisory",
    checkoutEnabled: true,
  },
    {
    id: "coaching-max",
    name: "Max Advisory",
    price: "6% of social media earnings",
    accent: "purple",
    icon: Crown,
    description: "A premium advisory tier for huge creators who want it all.",
    cta: "Apply Manually",
    checkoutEnabled: false,
  },
];

const FAQS = [
  {
    question: "How do your calls work?",
    answer:
      "The first call is an intro. We get acclimated with your content, see what your problems are, go through the basics and then make a plan for you to grow to a realistic average viewer amount of your choice. On our second call, we debrief and go over what worked along with actionable steps and what paid plan you'd be best for.",
  },
  {
    question: "Who are some people you've worked with?",
    answer:
      "We've worked with a few people of a decent size, some are lahmikejr, zephire, Indianapolis Colts WR Deion Burks, Komari, legostomper37, calshrimp and numerous others.",
  },
  {
    question: "What size of creators do you work with?",
    answer:
      "0-200 average Twitch/Kick viewers is our preferred area where we've been able to get clients the best results.",
  },
  {
    question: "Do I need a paid plan to use Kre8?",
    answer:
      "Marketplace browsing, messaging, profiles, and orders are free. The stats tracker is $10 USD/month after a 7-day card-required trial and renews automatically unless canceled. Other paid services are optional.",
  },
  {
    question: "Are refunds available?",
    answer:
      "No, they are not available. We do the first 2 call for free to see if you'd be a good fit, if you get enough value (which everyone does), you can pick a paid plan that would best suit you.",
  },
  {
    question: "Are paid plans required for editors or clippers to get clients?",
    answer:
      "No. Free editors and clippers can still be discovered and messaged. Pro plans are meant to help serious profiles stand out.",
  },
  {
    question: "Can creators use Kre8 without a subscription?",
    answer:
      "Yes. Creators can browse editors, message, place orders, and join clipping campaign early access without subscribing.",
  },
  {
    question: "What are one time services?",
    answer:
      "They are optional reviews or boosts you can purchase without committing to a monthly plan.",
  },
  {
    question: "What if I'm unsure about which plan is for me?",
    answer:
      "Reach us on Discord and we can setup a chat to figure out which is best for you.",
  },
];

function formatPrice(monthlyPrice, billing) {
  if (monthlyPrice === 0) return "$0";

  const price = billing === "yearly" ? monthlyPrice * 0.8 : monthlyPrice;
  const decimals = Number.isInteger(price) ? 0 : 2;

  return `$${price.toFixed(decimals)}`;
}

function GlowBg() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        className="absolute -left-32 top-12 h-[360px] w-[360px] rounded-full opacity-25 blur-3xl"
        style={{ background: `radial-gradient(circle, ${MINT}, transparent 62%)` }}
      />
      <div
        className="absolute right-[-7rem] top-60 h-[460px] w-[460px] rounded-full opacity-20 blur-3xl"
        style={{ background: `radial-gradient(circle, ${PURPLE}, transparent 62%)` }}
      />
      <div
        className="absolute bottom-10 left-1/2 h-[340px] w-[340px] -translate-x-1/2 rounded-full opacity-15 blur-3xl"
        style={{ background: `radial-gradient(circle, ${MINT}, transparent 64%)` }}
      />
    </div>
  );
}

function SectionHeader({ eyebrow, title, description }) {
  return (
    <div className="mx-auto max-w-3xl text-center">
      <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white/55">
        <Sparkles size={12} style={{ color: MINT }} />
        {eyebrow}
      </div>
      <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
        {title}
      </h2>
      {description && (
        <p className="mt-3 text-sm leading-relaxed text-white/58 sm:text-base">
          {description}
        </p>
      )}
    </div>
  );
}

function AudienceToggle({ audience, onChange }) {
  const options = [
    { value: "creators", label: "Creators", Icon: Video },
    { value: "editors", label: "Editors", Icon: Film },
  ];

  return (
    <div className="grid gap-2 rounded-3xl border border-white/10 bg-white/[0.03] p-2 backdrop-blur-xl sm:grid-cols-2">
      {options.map(({ value, label, Icon }) => {
        const active = audience === value;
        return (
          <button
            key={value}
            type="button"
            onClick={() => onChange(value)}
            className="flex min-h-[64px] items-center justify-center gap-3 rounded-2xl border px-4 py-3 text-sm font-bold transition"
            style={{
              borderColor: active ? `${MINT}80` : "rgba(255,255,255,0.08)",
              background: active
                ? "linear-gradient(135deg, rgba(16,185,129,0.18), rgba(168,85,247,0.14))"
                : "rgba(255,255,255,0.02)",
              color: active ? "white" : "rgba(255,255,255,0.62)",
              boxShadow: active ? `0 18px 50px -28px ${MINT}` : "none",
            }}
            aria-pressed={active}
          >
            <Icon size={18} />
            {label}
          </button>
        );
      })}
    </div>
  );
}

function BillingToggle({ billing, onChange }) {
  const options = [
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  return (
    <div className="inline-flex w-full max-w-sm rounded-full border border-white/10 bg-white/[0.03] p-1.5 backdrop-blur-xl sm:w-auto">
      {options.map((option) => {
        const active = billing === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className="relative flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full px-4 text-sm font-semibold transition sm:flex-none"
            style={{
              background: active ? "rgba(255,255,255,0.1)" : "transparent",
              color: active ? "white" : "rgba(255,255,255,0.55)",
            }}
            aria-pressed={active}
          >
            {option.label}
            {option.value === "yearly" && (
              <span className="rounded-full border border-purple-300/20 bg-purple-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-purple-100">
                Save 20%
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function FeatureList({ features, accent }) {
  const checkColor = accent === "purple" ? PURPLE : MINT;

  return (
    <ul className="mt-6 space-y-3">
      {features.map((feature) => (
        <li key={feature} className="flex gap-3 text-sm leading-relaxed text-white/68">
          <span
            className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border"
            style={{
              borderColor: `${checkColor}55`,
              background: `${checkColor}18`,
              color: checkColor,
            }}
          >
            <Check size={13} strokeWidth={3} />
          </span>
          <span>{feature}</span>
        </li>
      ))}
    </ul>
  );
}

function PricingCard({ plan, billing, isLoading, onSelect }) {
  const Icon = plan.icon;
  const isMint = plan.accent === "mint";
  const isPurple = plan.accent === "purple";
  const accentColor = isPurple ? PURPLE : isMint ? MINT : "rgba(255,255,255,0.22)";
  const period =
    plan.monthlyPrice === 0
      ? "free forever"
      : billing === "yearly"
      ? "per month, billed yearly"
      : "per month";

  return (
    <article
      className="relative flex h-full flex-col overflow-hidden rounded-3xl border bg-white/[0.03] p-5 shadow-[0_24px_70px_-40px_rgba(0,0,0,0.9)] backdrop-blur-xl transition duration-300 hover:-translate-y-1 sm:p-6"
      style={{
        borderColor: plan.badge
          ? "rgba(16,185,129,0.42)"
          : isPurple
          ? "rgba(168,85,247,0.28)"
          : isMint
          ? "rgba(16,185,129,0.26)"
          : "rgba(255,255,255,0.1)",
        boxShadow: isPurple
          ? "0 28px 90px -54px rgba(168,85,247,0.95)"
          : isMint
          ? "0 28px 90px -54px rgba(16,185,129,0.95)"
          : "0 24px 70px -46px rgba(255,255,255,0.18)",
      }}
    >
      {(isMint || isPurple) && (
        <div
          aria-hidden
          className="absolute inset-x-8 -top-20 h-36 rounded-full opacity-20 blur-3xl"
          style={{ background: accentColor }}
        />
      )}

      <div className="relative flex items-start justify-between gap-4">
        <div
          className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border"
          style={{
            borderColor: `${accentColor}44`,
            background: `${accentColor}18`,
            color: isMint || isPurple ? accentColor : "rgba(255,255,255,0.72)",
          }}
        >
          <Icon size={21} />
        </div>
        {plan.badge && (
          <span className="rounded-full border border-emerald-300/20 bg-emerald-500/15 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-emerald-100">
            {plan.badge}
          </span>
        )}
      </div>

      <div className="relative mt-5">
        <h3 className="text-xl font-bold text-white">{plan.name}</h3>
        <p className="mt-2 min-h-[44px] text-sm leading-relaxed text-white/55">
          {plan.description}
        </p>
      </div>

      <div className="relative mt-5">
        <div className="flex items-end gap-2">
          <span className="text-4xl font-black tracking-tight text-white">
            {formatPrice(plan.monthlyPrice, billing)}
          </span>
          {plan.monthlyPrice > 0 && <span className="pb-1 text-sm text-white/45">/mo</span>}
        </div>
        <p className="mt-1 text-xs font-medium uppercase tracking-wider text-white/38">
          {period}
        </p>
      </div>

      <FeatureList features={plan.features} accent={plan.accent} />

      <button
        type="button"
        onClick={() => onSelect(plan)}
        disabled={isLoading}
        className="relative mt-7 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-2xl px-4 text-center text-sm font-bold transition active:scale-[0.98]"
        style={{
          background:
            isMint || isPurple
              ? `linear-gradient(135deg, ${accentColor}, ${isPurple ? "#c084fc" : "#34d399"})`
              : "rgba(255,255,255,0.08)",
          color: isMint || isPurple ? "#050409" : "white",
          border: isMint || isPurple ? "1px solid transparent" : "1px solid rgba(255,255,255,0.1)",
          boxShadow: isMint || isPurple ? `0 18px 54px -24px ${accentColor}` : "none",
          opacity: isLoading ? 0.72 : 1,
          cursor: isLoading ? "wait" : "pointer",
        }}
      >
        {isLoading ? "Starting checkout..." : plan.cta}
        <ArrowRight size={16} />
      </button>
    </article>
  );
}

function OneTimeServiceCard({ service, isLoading, onSelect }) {
  const Icon = service.icon;

  return (
    <article className="flex h-full flex-col rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl transition hover:border-white/20 hover:bg-white/[0.045] sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="grid h-11 w-11 place-items-center rounded-2xl border border-purple-300/20 bg-purple-500/10 text-purple-100">
          <Icon size={20} />
        </div>
        <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs font-semibold text-white/58">
          {service.audience}
        </span>
      </div>
      <h3 className="mt-5 text-lg font-bold text-white">{service.title}</h3>
      <p className="mt-2 text-3xl font-black tracking-tight text-white">{service.price}</p>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-white/58">
        {service.description}
      </p>
      <button
        type="button"
        onClick={() => onSelect(service)}
        disabled={isLoading}
        className="mt-6 inline-flex min-h-[46px] items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.05] px-4 text-sm font-bold text-white transition hover:bg-white/[0.08] disabled:cursor-wait disabled:opacity-70"
      >
        {isLoading ? "Starting checkout..." : service.cta}
        <ArrowRight size={15} />
      </button>
    </article>
  );
}

function CoachingPlanCard({ plan, isLoading, onSelect }) {
  const Icon = plan.icon;
  const accentColor =
    plan.accent === "purple"
      ? PURPLE
      : plan.accent === "mint"
      ? MINT
      : "rgba(255,255,255,0.22)";

  return (
    <article className="flex h-full flex-col rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div
          className="grid h-11 w-11 place-items-center rounded-2xl border"
          style={{
            borderColor: `${accentColor}44`,
            background: `${accentColor}18`,
            color: plan.accent === "neutral" ? "rgba(255,255,255,0.72)" : accentColor,
          }}
        >
          <Icon className="mx-auto" size={20} />
        </div>
      </div>
      <h3 className="mt-5 text-lg font-bold text-white">{plan.name}</h3>
      <p className="mt-3 text-sm leading-relaxed text-white/58">{plan.description}</p>
      <div className="mt-auto pt-6">
        <button
          type="button"
          onClick={() => onSelect(plan)}
          disabled={isLoading}
          className="inline-flex min-h-[46px] w-full items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.05] px-4 text-sm font-bold text-white transition hover:bg-white/[0.08] disabled:cursor-wait disabled:opacity-70"
        >
          {isLoading ? "Starting checkout..." : plan.cta}
          <ArrowRight size={15} />
        </button>
      </div>
    </article>
  );
}

function PricingFAQ() {
  return (
    <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:py-20">
      <SectionHeader
        eyebrow="Questions"
        title="Straight answers before you upgrade"
        description="Paid plans are optional. Kre8 should be useful before you spend anything."
      />
      <div className="mt-8 space-y-3">
        {FAQS.map((item) => (
          <details
            key={item.question}
            className="group rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl"
          >
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left text-sm font-bold text-white sm:text-base">
              <span>{item.question}</span>
              <HelpCircle
                size={18}
                className="shrink-0 text-white/35 transition group-open:text-emerald-300"
              />
            </summary>
            <p className="mt-3 text-sm leading-relaxed text-white/58">{item.answer}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function FinalPricingCTA() {
  return (
    <section className="px-4 pb-16 sm:px-6 lg:pb-24">
      <div className="mx-auto max-w-6xl overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-6 text-center shadow-[0_24px_90px_-55px_rgba(168,85,247,0.9)] backdrop-blur-xl sm:p-10">
        <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-2xl border border-emerald-300/20 bg-emerald-500/10 text-emerald-200">
          <Zap size={22} />
        </div>
        <h2 className="text-2xl font-bold tracking-tight text-white sm:text-4xl">
          Start free. Upgrade only when it helps you grow.
        </h2>
        <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
          <Link
            to="/signup"
            className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-emerald-400 px-5 text-sm font-black text-[#050409] transition hover:bg-emerald-300 active:scale-[0.98]"
          >
            Join Kre8
            <ArrowRight size={16} />
          </Link>
          <Link
            to="/gaming-video-editors"
            className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.05] px-5 text-sm font-bold text-white transition hover:bg-white/[0.08]"
          >
            Browse Editors
            <Eye size={16} />
          </Link>
          <Link
            to="/clipping-campaigns"
            className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.05] px-5 text-sm font-bold text-white transition hover:bg-white/[0.08]"
          >
            Clipping Campaigns
            <Scissors size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser, activeUser } = useAuth();
  const [audience, setAudience] = useState("creators");
  const [billing, setBilling] = useState("monthly");
  const [notice, setNotice] = useState(null);
  const [checkoutId, setCheckoutId] = useState("");
  const noticeTimeoutRef = useRef(null);
  const contactStartedRef = useRef(false);
  // const plans = PLAN_DATA[audience];

  const showNotice = useCallback((message, type = "success") => {
    setNotice({ message, type });
    window.clearTimeout(noticeTimeoutRef.current);
    noticeTimeoutRef.current = window.setTimeout(() => setNotice(null), 4200);
  }, []);

  useEffect(() => {
    const toast = getCheckoutStatusToast(searchParams);
    if (!toast) return;

    showNotice(toast.message, toast.type);
    setSearchParams(clearCheckoutStatusParams(searchParams), { replace: true });
  }, [searchParams, setSearchParams, showNotice]);

  const redirectToSignup = (params = {}) => {
    const search = new URLSearchParams(params);
    navigate(`/signup${search.toString() ? `?${search.toString()}` : ""}`);
  };

  const openMaxAdvisoryChat = useCallback(async () => {
    if (!currentUser) {
      const next = encodeURIComponent("/pricing?contact=max-advisory");
      navigate(`/login?next=${next}`);
      return;
    }

    setCheckoutId("coaching-max");

    try {
      const conversationId = await getOrCreateDirectConversation({
        db,
        currentUserId: currentUser.uid,
        targetUserId: COACHING_ACCOUNT_ID,
      });
      const params = new URLSearchParams({
        conversationId,
        draft: MAX_ADVISORY_DRAFT,
      });
      navigate(`/chat?${params.toString()}`);
    } catch (error) {
      showNotice(
        error.message || "Unable to open the Max Advisory conversation.",
        "error"
      );
      setCheckoutId("");
    }
  }, [currentUser, navigate, showNotice]);

  useEffect(() => {
    if (
      searchParams.get("contact") !== "max-advisory" ||
      !currentUser ||
      contactStartedRef.current
    ) {
      return;
    }

    contactStartedRef.current = true;
    openMaxAdvisoryChat();
  }, [currentUser, openMaxAdvisoryChat, searchParams]);

  const startCheckout = async ({ id, path, payload, successMessage }) => {
    setCheckoutId(id);
    showNotice(successMessage);

    try {
      const session = await createPricingCheckoutSession({
        endpoint: process.env.REACT_APP_ENDPOINT,
        path,
        payload,
      });

      assignSafeExternalUrl(session.url);
    } catch (error) {
      showNotice(
        error.message || "Unable to start checkout. Please try again.",
        "error"
      );
      setCheckoutId("");
    }
  };

  const handlePlanSelect = async (plan) => {
    if (!currentUser) {
      redirectToSignup({
        plan: toBackendPlanId(plan.id),
        billing,
      });
      return;
    }

    if (plan.monthlyPrice === 0) {
      showNotice(`${plan.name} is already available for free.`);
      return;
    }

    const mismatchMessage = getRoleMismatchMessage({
      userRole: getUserRole(activeUser),
      requiredRole: getRequiredRoleForPlan(toBackendPlanId(plan.id)),
      itemName: plan.name,
    });
    if (mismatchMessage) {
      showNotice(mismatchMessage, "error");
      return;
    }

    await startCheckout({
      id: plan.id,
      path: "/api/stripe/create-subscription-checkout",
      payload: {
        uid: currentUser.uid,
        planId: toBackendPlanId(plan.id),
        billingInterval: billing,
      },
      successMessage: `Starting checkout: ${plan.name} will unlock ${
        PLAN_PREVIEWS[plan.id] || "paid Kre8 features."
      }`,
    });
  };

  const handleServiceSelect = async (service) => {
    if (!currentUser) {
      redirectToSignup({
        product: toBackendProductId(service.id),
      });
      return;
    }

    const mismatchMessage = getRoleMismatchMessage({
      userRole: getUserRole(activeUser),
      requiredRole: getRequiredRoleForProduct(toBackendProductId(service.id)),
      itemName: service.title,
    });
    if (mismatchMessage) {
      showNotice(mismatchMessage, "error");
      return;
    }

    await startCheckout({
      id: service.id,
      path: "/api/stripe/create-purchase-checkout",
      payload: {
        uid: currentUser.uid,
        productId: toBackendProductId(service.id),
      },
      successMessage: `Starting checkout: ${service.title} will ${
        SERVICE_PREVIEWS[service.id] || "create a purchase request."
      }`,
    });
  };

  const handleCoachingSelect = async (plan) => {
    if (!plan.checkoutEnabled) {
      await openMaxAdvisoryChat();
      return;
    }

    if (!currentUser) {
      redirectToSignup({
        plan: toBackendPlanId(plan.id),
        billing: "monthly",
      });
      return;
    }

    const mismatchMessage = getRoleMismatchMessage({
      userRole: getUserRole(activeUser),
      requiredRole: getRequiredRoleForPlan(toBackendPlanId(plan.id)),
      itemName: plan.name,
    });
    if (mismatchMessage) {
      showNotice(mismatchMessage, "error");
      return;
    }

    await startCheckout({
      id: plan.id,
      path: "/api/stripe/create-subscription-checkout",
      payload: {
        uid: currentUser.uid,
        planId: toBackendPlanId(plan.id),
        billingInterval: "monthly",
      },
      successMessage: `Starting checkout: ${plan.name} will unlock ${
        PLAN_PREVIEWS[plan.id] || "coaching access."
      }`,
    });
  };

  return (
    <main className="relative min-h-screen bg-[#07060d] text-white">
      <SEO
        title="Pricing | Kre8"
        description="Free and paid Kre8 plans for creators, editors, and clippers."
        canonicalPath="/pricing"
      />
      <TheNav />
      <GlowBg />

      <section className="relative mx-auto max-w-6xl px-4 pb-4 pt-12 sm:px-6 sm:pb-4 sm:pt-16 lg:pb-4 lg:pt-20">
        {notice && (
          <div
            className={`mx-auto mb-6 max-w-3xl rounded-2xl border px-4 py-3 text-sm font-semibold leading-relaxed shadow-[0_18px_60px_-35px_rgba(16,185,129,0.9)] backdrop-blur-xl ${
              notice.type === "error"
                ? "border-red-300/20 bg-red-500/15 text-red-100"
                : "border-emerald-300/20 bg-emerald-500/15 text-emerald-100"
            }`}
          >
            {notice.message}
          </div>
        )}
        <div className="mx-auto max-w-4xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white/60 backdrop-blur-xl">
            <Sparkles size={13} style={{ color: MINT }} />
            Stats tracker · 7-day free trial
          </div>
          <h1 className="text-4xl font-black tracking-tight text-white sm:text-5xl lg:text-6xl">
            Your content stats. One simple monthly plan.
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-white/62 sm:text-lg">
            Compare TikTok, Instagram Reels, and Twitch in one place. Marketplace accounts stay free; the stats tracker is a separate subscription.
          </p>
          {/* <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-emerald-300/20 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-100">
            <Check size={15} />
            Free to start for creators, editors, and clippers. Paid upgrades are optional.
          </div> */}
        </div>

        <section aria-label="Stats tracker pricing" className="mx-auto mt-8 max-w-xl rounded-2xl border border-purple-400/25 bg-purple-500/10 p-6 text-center sm:p-8">
          <h2 className="text-xl font-semibold">Social Media Stats Tracker</h2><p className="mt-4 text-4xl font-bold">$10 <span className="text-base font-normal text-white/60">USD/month</span></p>
          <p className="mt-3 text-sm text-white/70">All three platforms. All available tracker features.</p>
          <p className="mt-4 text-sm leading-relaxed text-white/60">Start with a 7-day free trial. Card required. Your card is automatically charged $10 USD/month after your trial unless you cancel before it ends. Cancel anytime and keep access until the trial or paid period ends.</p>
          <Link to={currentUser ? "/stats" : "/signup?next=%2Fstats"} className="mt-6 inline-flex min-h-11 items-center justify-center rounded-lg bg-[#9146ff] px-5 py-3 text-sm font-semibold text-white">Try the tracker free for 7 days</Link>
          <p className="mt-3 text-xs text-white/45">One trial per account, including existing users. We’ll email a reminder 3 days before the trial ends. Available data depends on platform access.</p>
        </section>
        {/* <div className="mx-auto mt-10 max-w-3xl">
          <AudienceToggle audience={audience} onChange={setAudience} />
        </div>

        <div className="mt-6 flex justify-center">
          <BillingToggle billing={billing} onChange={setBilling} />
        </div> */}

        {/* <div className="mt-9 grid gap-4 lg:grid-cols-3">
          {plans.map((plan) => (
            <PricingCard
              key={plan.id}
              plan={plan}
              billing={billing}
              isLoading={checkoutId === plan.id}
              onSelect={handlePlanSelect}
            />
          ))}
        </div> */}

        {/* <div className="mx-auto mt-8 grid gap-3 text-sm text-white/48 sm:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-4">
            Built for creators, editors, and clippers who want better workflows.
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-4">
            Get a stronger profile before more campaigns and creators arrive.
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-4">
            Early users get the biggest visibility advantage.
          </div>
        </div> */}
      </section>

      <section className="relative mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:py-20">
        <SectionHeader
          eyebrow="One time services"
          title="Extra help without a subscription"
          description="Optional reviews and boosts for creators, editors, and clippers who want a sharper profile, clearer workflow, or more visibility."
        />
        <div className="mt-9 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-center">
          {ONE_TIME_SERVICES.map((service) => (
            <OneTimeServiceCard
              key={service.id}
              service={service}
              isLoading={checkoutId === service.id}
              onSelect={handleServiceSelect}
            />
          ))}
        </div>
      </section>

      <section className="relative mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:py-20">
        <SectionHeader
          eyebrow="1:1 Coaching"
          title="Coaching plans for creators at different stages"
          description="Optional creator coaching stays separate from editing and clipping. Use it only if you want deeper growth help."
        />
        <div className="mt-9 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          {COACHING_PLANS.map((plan) => (
            <CoachingPlanCard
              key={plan.id}
              plan={plan}
              isLoading={checkoutId === plan.id}
              onSelect={handleCoachingSelect}
            />
          ))}
        </div>
      </section>

      <PricingFAQ />
      <FinalPricingCTA />
    </main>
  );
}
